'use client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { ChevronDown, ChevronUp, Package, AlertCircle, CheckCircle2, Circle } from 'lucide-react';
import { Link, useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from '../../AbstractElements';
import { useLoader } from '../../_helper/LoaderContext';
import { createTestOrder, generateTestReport, getTestResult, uploadReportOcr } from './service/report-test-service';
import { viewDoctor } from '../DoctorManagment/service/doctor-service';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import errorProfile from "../.././assets/images/blank_profile.png";
import { FiPhoneCall } from "react-icons/fi";
import { TfiEmail } from "react-icons/tfi";
import S3ImageLoader from "../../S3ImageLoader";
import { toast } from 'react-toastify';
import ReportList from './ReportList';
import { Modal, Button } from "react-bootstrap";
import { Tooltip } from 'react-tooltip';

// SMART REFERENCE RANGE
// const getReferenceRange = (ranges = [], gender, age) => {
//     if (!Array.isArray(ranges) || ranges.length === 0) return 'N/A';
//     const normalizedGender = (gender || 'male').toLowerCase();
//     const isAdult = (age || 0) >= 18;
//     let match = ranges.find(r => {
//         const rg = (r.gender || 'all').toLowerCase();
//         const matchesGender = rg === normalizedGender || rg === 'all';
//         const matchesAge =
//             (r.ageCategory === 'adult' && isAdult) ||
//             (r.ageCategory === 'child' && !isAdult) ||
//             r.ageCategory === 'all';
//         return matchesGender && matchesAge;
//     });
//     if (!match) {
//         match = ranges.find(r => (r.gender || 'all').toLowerCase() === 'all');
//     }
//     if (!match) return 'N/A';
//     if (match.qualitativeValue !== null && match.qualitativeValue !== undefined && match.qualitativeValue !== '') return match.qualitativeValue.trim();
//     if (match.ratioValue !== null && match.ratioValue !== undefined && match.ratioValue !== '') return match.ratioValue.trim();
//     if (match.scaleValue !== null && match.scaleValue !== undefined && match.scaleValue !== '') return match.scaleValue.trim();
//     if (match.minValue !== null && match.maxValue !== null) return `${match.minValue} - ${match.maxValue}`;
//     if (match.minValue !== null) return `≥ ${match.minValue}`;
//     if (match.maxValue !== null) return `≤ ${match.maxValue}`;
//     if (match.scaleNote) return match.scaleNote.trim();
//     return 'N/A';
// };

// SMART REFERENCE RANGE — Fixed for qualitative, ratio, scale values
const getReferenceRange = (ranges = [], gender, age) => {
    if (!Array.isArray(ranges) || ranges.length === 0) return 'N/A';

    const normalizedGender = (gender || 'male').toLowerCase();
    const isAdult = (age || 0) >= 18;

    // Helper: Find best match with age + gender priority
    const findBestMatch = (filterCondition) => {
        return ranges.find(r => {
            const rg = (r.gender || 'all').toLowerCase();
            const matchesGender = rg === normalizedGender || rg === 'all';
            const matchesAge =
                (r.ageCategory === 'adult' && isAdult) ||
                (r.ageCategory === 'child' && !isAdult) ||
                r.ageCategory === 'all';
            return filterCondition(r) && matchesGender && matchesAge;
        }) ||
            ranges.find(r => {
                const rg = (r.gender || 'all').toLowerCase();
                const matchesGender = rg === 'all';
                return filterCondition(r) && matchesGender;
            });
    };

    // 1. Qualitative value — highest priority, ignore age/gender for matching if possible
    if (ranges.some(r => r.qualitativeValue?.trim())) {
        const match = findBestMatch(r => r.qualitativeValue?.trim());
        if (match?.qualitativeValue?.trim()) {
            return match.qualitativeValue.trim();
        }
    }

    // 2. Ratio value (e.g., "1:80")
    if (ranges.some(r => r.ratioValue?.trim())) {
        const match = findBestMatch(r => r.ratioValue?.trim());
        if (match?.ratioValue?.trim()) {
            return match.ratioValue.trim();
        }
    }

    // 3. Scale value (e.g., "Trace", "1+", "2+")
    if (ranges.some(r => r.scaleValue?.trim())) {
        const match = findBestMatch(r => r.scaleValue?.trim());
        if (match?.scaleValue?.trim()) {
            return match.scaleValue.trim();
        }
    }

    // 4. Scale note (e.g., "Seen", "Not Seen")
    if (ranges.some(r => r.scaleNote?.trim())) {
        const match = findBestMatch(r => r.scaleNote?.trim());
        if (match?.scaleNote?.trim()) {
            return match.scaleNote.trim();
        }
    }

    // 5. Only now apply strict numeric range with age/gender logic
    const numericMatch = ranges.find(r => {
        const rg = (r.gender || 'all').toLowerCase();
        const matchesGender = rg === normalizedGender || rg === 'all';
        const matchesAge =
            (r.ageCategory === 'adult' && isAdult) ||
            (r.ageCategory === 'child' && !isAdult) ||
            r.ageCategory === 'all';
        return matchesGender && matchesAge && (r.minValue !== null || r.maxValue !== null);
    });

    if (numericMatch) {
        if (numericMatch.minValue !== null && numericMatch.maxValue !== null) {
            return `${numericMatch.minValue} - ${numericMatch.maxValue}`;
        }
        if (numericMatch.minValue !== null) {
            return `≥ ${numericMatch.minValue}`;
        }
        if (numericMatch.maxValue !== null) {
            return `≤ ${numericMatch.maxValue}`;
        }
    }

    // Final fallback to any "all" entry
    const fallback = ranges.find(r => (r.gender || 'all').toLowerCase() === 'all');
    if (fallback) {
        if (fallback.qualitativeValue?.trim()) return fallback.qualitativeValue.trim();
        if (fallback.ratioValue?.trim()) return fallback.ratioValue.trim();
        if (fallback.scaleValue?.trim()) return fallback.scaleValue.trim();
        if (fallback.scaleNote?.trim()) return fallback.scaleNote.trim();
        if (fallback.minValue !== null && fallback.maxValue !== null) return `${fallback.minValue} - ${fallback.maxValue}`;
    }

    return 'N/A';
};

const checkOutOfRange = (value, range) => {
    if (!value || !range) return false;

    const num = parseFloat(value);
    if (isNaN(num)) return false;

    // Match "10 - 20"
    const rangeMatch = range.match(/^(\d+\.?\d*)\s*-\s*(\d+\.?\d*)$/);
    if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        return num < min || num > max;
    }

    // Match "≥ 10"
    const minMatch = range.match(/^≥\s*(\d+\.?\d*)$/);
    if (minMatch) {
        const min = parseFloat(minMatch[1]);
        return num < min;
    }

    // Match "≤ 20"
    const maxMatch = range.match(/^≤\s*(\d+\.?\d*)$/);
    if (maxMatch) {
        const max = parseFloat(maxMatch[1]);
        return num > max;
    }

    return false;
};

const asDoctorId = (value) => {
    if (!value || value === "undefined" || value === "null") return "";
    if (typeof value === "object") {
        return asDoctorId(value._id || value.doctorId || value.doctor_id);
    }
    return String(value);
};

const resolveDoctorRecordId = (doctor) => {
    if (!doctor) return "";
    if (Object.prototype.hasOwnProperty.call(doctor, "doctorId") || doctor.doctor_id) {
        return asDoctorId(doctor.doctorId || doctor.doctor_id);
    }
    return asDoctorId(doctor._id || doctor.id);
};

const resolveClinicDoctor = async (doctor) => {
    if (!doctor || doctor.isClinicDoctor !== true) return doctor;
    if (doctor.signature || doctor.signatureImage) return doctor;

    const doctorId = resolveDoctorRecordId(doctor);
    if (!doctorId) return doctor;

    try {
        const detailRes = await viewDoctor(doctorId, { isClinicDoctor: true });
        const detail = detailRes?.data?.data;
        if (!detailRes?.data?.success || !detail) return doctor;
        const filledDetail = Object.fromEntries(
            Object.entries(detail).filter(([, value]) => value !== null && value !== undefined && value !== "")
        );
        return { ...doctor, ...filledDetail, isClinicDoctor: true };
    } catch (error) {
        console.log(error);
        return doctor;
    }
};

const clinicDoctorPayload = (doctor) => {
    if (!doctor || doctor.isClinicDoctor !== true) return {};

    const name = doctor.name || `${doctor.fname || ""} ${doctor.lname || ""}`.trim();
    return {
        isClinicDoctor: true,
        doctor: {
            doctorId: resolveDoctorRecordId(doctor),
            name,
            fname: doctor.fname || "",
            lname: doctor.lname || "",
            email: doctor.email || "",
            mobile: doctor.mobile || doctor.mobileNumber || "",
            mobileNumber: doctor.mobileNumber || doctor.mobile || "",
            specialization: doctor.specialization || doctor.degree || "",
            degree: doctor.degree || doctor.specialization || "",
            hospitalName: doctor.hospitalName || "",
            address: doctor.address || "",
            signature: doctor.signature || doctor.signatureImage || "",
            signatureImage: doctor.signatureImage || doctor.signature || "",
            signatureName: doctor.signatureName || name,
            signatureQualification: doctor.signatureQualification || doctor.degree || doctor.specialization || "",
            profileImage: doctor.profileImage || doctor.profilePicture || "",
            commissionPercentage: doctor.commissionPercentage ?? doctor.commissionPercent ?? 0,
            isClinicDoctor: true,
        },
    };
};

export default function AddTestResult() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [apiData, setApiData] = useState(null);
    const [expandedTests, setExpandedTests] = useState({});
    const [expandedPackages, setExpandedPackages] = useState({});
    const { setLoading } = useLoader();
    const [testNotes, setTestNotes] = useState({});
    const [highlightedParams, setHighlightedParams] = useState({});
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [reportTypes, setReportTypes] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [completedTests, setCompletedTests] = useState(new Set());
    const handleCloseResult = () => {
        setShowResult(false)
        navigate(`${process.env.PUBLIC_URL}/test-list`);
    };

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        trigger,
        formState: { errors },
    } = useForm({
        mode: "onChange",
        shouldUnregister: false,
    });

    useEffect(() => {
        fetchTestData();
    }, [id]);

    const fetchTestData = async () => {
        setLoading(true);
        try {
            const res = await getTestResult(id);
            if (res.data.success) {
                setApiData(res.data);
                setData(prev => ({ ...prev, ...res.data.data }));

                const initialNotes = {};
                const initialHighlights = {};
                const initialReportTypes = {};
                const testExpansion = {};
                const packageExpansion = {};

                res.data.data.allTests.forEach(test => {
                    initialReportTypes[test._id] = test.reportType || test.report_type || 'provisional';

                    // Notes
                    if (test.reportNote) {
                        initialNotes[test._id] = test.reportNote;
                    }


                    // Auto-expand all tests inside packages
                    if (test.isPackage) {
                        testExpansion[test._id] = true;
                        if (test.packageId) {
                            packageExpansion[test.packageId._id] = true;
                        }
                    }

                    // Traverse parameters to pre-fill values
                    test.parameterGroups?.forEach(group => {
                        group.parameters?.forEach(param => {
                            const fieldName = test.isPackage
                                ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                                : `standalone_${test._id}_param_${param.parameterId}`;

                            // Pre-fill existing result values
                            if (param.resultValue) {
                                setValue(fieldName, param.resultValue);
                            }

                            // Set highlight state
                            if (param.isHighlight !== undefined) {
                                initialHighlights[fieldName] = param.isHighlight;
                            }
                        });
                    });

                    const preCompleted = new Set();
                    let allFilled = true;
                    test.parameterGroups?.forEach(group => {
                        group.parameters?.forEach(param => {
                            if (!param.resultValue || param.resultValue.trim() === '') {
                                allFilled = false;
                            }
                        });
                    });

                    if (allFilled) {
                        preCompleted.add(test._id);
                    }
                    setCompletedTests(preCompleted);
                });

                setTestNotes(initialNotes);
                setHighlightedParams(initialHighlights);
                setReportTypes(initialReportTypes);
                setExpandedTests(prev => ({ ...prev, ...testExpansion }));
                setExpandedPackages(prev => ({ ...prev, ...packageExpansion }));
            }
        } catch (error) {
            console.error("Error:", error);
            toast.warning(t("Failed to load test data"));
        } finally {
            setLoading(false);
        }
    };

    const importFileButton = (
        <button
            type="button"
            className="btn btn-primary"
            onClick={() => toast.error(t("You are not having permission to do this task."))}
        >
            {t("Import File")}
        </button>
    );

    if (!apiData?.data) {
        return null;
    }

    const patient = apiData.data?.patient || {};
    const allTests = apiData.data?.allTests || [];
    const isHospitalPatient = apiData.data?.isHospitalPatient === true || data?.isHospitalPatient === true;
    const gender = (patient.gender || 'male').toLowerCase();
    const age = parseInt(patient.age) || 0;

    const toggleTest = (testId) => {
        setExpandedTests(prev => ({ ...prev, [testId]: !prev[testId] }));
    };

    const togglePackage = (packageId) => {
        setExpandedPackages(prev => ({ ...prev, [packageId]: !prev[packageId] }));
    };

    const toggleHighlight = (fieldName) => {
        setHighlightedParams(prev => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const getTestReportType = (testId) => reportTypes[testId] || 'provisional';

    const setTestReportType = (testId, type) => {
        setReportTypes(prev => ({ ...prev, [testId]: type }));
    };

    const collectTestFieldNames = (test) => {
        const fieldNames = [];
        test.parameterGroups?.forEach(group => {
            group.parameters?.forEach(param => {
                const fieldName = test.isPackage
                    ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                    : `standalone_${test._id}_param_${param.parameterId}`;
                fieldNames.push(fieldName);
            });
        });
        return fieldNames;
    };

    const buildSingleTestPayload = (test, values, reportDoctor) => ({
        ...clinicDoctorPayload(reportDoctor),
        allTests: [{
            _id: test._id,
            parameterGroups: test.parameterGroups.map(group => ({
                _id: group._id,
                parameters: group.parameters.map(param => {
                    const fieldName = test.isPackage
                        ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                        : `standalone_${test._id}_param_${param.parameterId}`;

                    return {
                        _id: param._id,
                        parameterId: param.parameterId,
                        name: param.name,
                        resultValue: (values[fieldName] || "").toString().trim(),
                        isHighlight: !!highlightedParams[fieldName]
                    };
                })
            })),
            reportNote: testNotes[test._id] || ""
        }]
    });

    // const saveSingleTest = async (testId) => {
    //     const test = allTests.find(t => t._id === testId);
    //     if (!test) return;

    //     setLoading(true);
    //     try {
    //         const values = getValues();

    //         const payload = {
    //             allTests: [{
    //                 _id: test._id,
    //                 parameterGroups: test.parameterGroups.map(group => ({
    //                     _id: group._id,
    //                     parameters: group.parameters.map(param => {
    //                         const fieldName = test.isPackage
    //                             ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                             : `standalone_${test._id}_param_${param.parameterId}`;

    //                         return {
    //                             _id: param._id,
    //                             parameterId: param.parameterId,
    //                             name: param.name,
    //                             resultValue: (values[fieldName] || "").toString().trim(),
    //                             isHighlight: !!highlightedParams[fieldName]
    //                         };
    //                     })
    //                 })),
    //                 reportNote: testNotes[test._id] || ""
    //             }]
    //         };

    //         const res = await createTestOrder(id, payload);
    //         if (res.data.success) {
    //             toast.success(res.data.message || "Test saved successfully!");
    //         }
    //     } catch (error) {
    //         console.error(error);
    //         toast.warning("Failed to save test");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleResultChange = (fieldName, value, range) => {
        const isOut = checkOutOfRange(value, range);

        setHighlightedParams(prev => ({
            ...prev,
            [fieldName]: isOut ? true : false
        }));
    };

    const saveSingleTest = async (testId) => {
        const test = allTests.find(t => t._id === testId);
        if (!test) return false;

        const isValid = await trigger(collectTestFieldNames(test));
        if (!isValid) {
            toast.warning(t("Please fill all required fields in this test!"));
            return false;
        }

        setLoading(true);
        try {
            const values = getValues();
            const reportDoctor = await resolveClinicDoctor(apiData?.data?.doctor);
            const res = await createTestOrder(id, buildSingleTestPayload(test, values, reportDoctor));
            if (res.data.success) {
                toast.success(res.data.message || t("Test saved successfully!"));
                return true;
            }
            toast.warning(res.data.message || t("Failed to save test"));
            return false;
        } catch (error) {
            console.error(error);
            toast.warning(t("Failed to save test"));
            return false;
        } finally {
            setLoading(false);
        }
    };

    // const saveSingleTest = async (testId) => {
    //     const test = allTests.find(t => t._id === testId);
    //     if (!test) return;

    //     const fieldNames = [];
    //     test.parameterGroups?.forEach(group => {
    //         group.parameters?.forEach(param => {
    //             const fieldName = test.isPackage
    //                 ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                 : `standalone_${test._id}_param_${param.parameterId}`;
    //             fieldNames.push(fieldName);
    //         });
    //     });

    //     const isValid = await trigger(fieldNames);
    //     if (!isValid) {
    //         toast.warning("Please fill all required fields in this test!");
    //         return;
    //     }

    //     setLoading(true);
    //     try {
    //         const values = getValues();

    //         const payload = {
    //             allTests: [{
    //                 _id: test._id,
    //                 parameterGroups: test.parameterGroups.map(group => ({
    //                     _id: group._id,
    //                     parameters: group.parameters.map(param => {
    //                         const fieldName = test.isPackage
    //                             ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                             : `standalone_${test._id}_param_${param.parameterId}`;

    //                         return {
    //                             _id: param._id,
    //                             parameterId: param.parameterId,
    //                             name: param.name,
    //                             resultValue: (values[fieldName] || "").toString().trim(),
    //                             isHighlight: !!highlightedParams[fieldName]
    //                         };
    //                     })
    //                 })),
    //                 reportNote: testNotes[test._id] || ""
    //             }]
    //         };

    //         const res = await createTestOrder(id, payload);
    //         if (res.data.success) {
    //             toast.success(res.data.message || "Test saved successfully!");

    //             // Mark this test as completed (since save succeeded and validation passed)
    //             setCompletedTests(prev => {
    //                 const newSet = new Set(prev);
    //                 newSet.add(test._id);
    //                 return newSet;
    //             });

    //             // Check if ALL tests are now completed
    //             const totalTests = allTests.length;
    //             const completedCount = [...completedTests].length + 1; // +1 for current

    //             if (completedCount === totalTests) {
    //                 // toast.info("All tests completed! Generating report...");
    //                 await generateReport();
    //             }
    //         }
    //     } catch (error) {
    //         console.error(error);
    //         toast.warning(error.response?.data?.message || "Failed to save test");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    // const onGlobalSubmit = async () => {
    //     const isValid = await trigger();
    //     if (!isValid) {
    //         toast.warning("Please fill all required fields!");
    //         return;
    //     }

    //     const values = getValues();
    //     setLoading(true);
    //     try {
    //         const payload = {
    //             allTests: allTests.map(test => ({
    //                 _id: test._id,
    //                 parameterGroups: test.parameterGroups.map(group => ({
    //                     _id: group._id,
    //                     parameters: group.parameters.map(param => {
    //                         const fieldName = test.isPackage
    //                             ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
    //                             : `standalone_${test._id}_param_${param.parameterId}`;

    //                         return {
    //                             _id: param._id,
    //                             parameterId: param.parameterId,
    //                             name: param.name,
    //                             resultValue: (values[fieldName] || "").toString().trim(),
    //                             isHighlight: !!highlightedParams[fieldName]
    //                         };
    //                     })
    //                 })),
    //                 reportNote: testNotes[test._id] || ""
    //             }))
    //         };

    //         const res = await createTestOrder(id, payload);
    //         if (res.data.success) {
    //             // toast.success(res.data.message || "All results saved successfully!");
    //             await generateReport();
    //         }
    //     } catch (error) {
    //         console.error(error);
    //         toast.warning("Failed to submit results");
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const extractReportUrl = (res) => {
        const dataPayload = res?.data?.data;
        if (Array.isArray(dataPayload)) {
            const match = dataPayload.find(item => item?.reportPdfPath || item?.report_url || item?.reportUrl);
            if (match) {
                return match.reportPdfPath || match.report_url || match.reportUrl;
            }
            if (typeof dataPayload[0] === 'string') return dataPayload[0];
        }
        return (
            dataPayload?.reportPdfPath ||
            dataPayload?.report_url ||
            dataPayload?.reportUrl ||
            dataPayload?.url ||
            dataPayload?.pdfPath ||
            dataPayload?.path ||
            (typeof dataPayload === 'string' ? dataPayload : null) ||
            res?.data?.reportPdfPath ||
            res?.data?.report_url ||
            res?.data?.reportUrl
        );
    };

    const extractPatientId = (res) => {
        const dataPayload = res?.data?.data;
        if (Array.isArray(dataPayload)) {
            const match = dataPayload.find(item => item?.patientId);
            if (match?.patientId) return match.patientId;
        }
        return (
            dataPayload?.patientId ||
            apiData?.data?.patient?.patientId ||
            data?.patient?.patientId ||
            apiData?.data?.patientpatientId ||
            data?.patientpatientId ||
            apiData?.data?.patientId ||
            data?.patientId ||
            apiData?.data?.patient?._id ||
            data?.patient?._id
        );
    };

    const uploadHospitalOcr = async (res, selectedType) => {
        const reportUrl = extractReportUrl(res);
        const patientId =
            extractPatientId(res) ||
            apiData?.data?.patient?.patientId ||
            data?.patient?.patientId ||
            apiData?.data?.patientpatientId ||
            data?.patientpatientId ||
            apiData?.data?.patientId ||
            data?.patientId ||
            apiData?.data?.patient?._id ||
            data?.patient?._id;

        if (!reportUrl) {
            toast.error(t("Generated report URL is missing."));
            return false;
        }

        if (!patientId) {
            toast.error(t("Patient ID is missing for OCR upload."));
            return false;
        }

        const doctor = apiData?.data?.doctor || data?.doctor;
        const doctorId = resolveDoctorRecordId(doctor);

        if (!doctorId) {
            toast.error(t("Doctor ID is missing for OCR upload."));
            return false;
        }

        try {
            const ocrRes = await uploadReportOcr({
                patient_id: patientId,
                doctor_id: doctorId,
                report_type: selectedType,
                report_url: reportUrl,
            });

            if (!ocrRes?.data?.success) {
                toast.error(ocrRes?.data?.message || t("Failed to upload OCR report"));
                return false;
            }
            return true;
        } catch (ocrError) {
            console.error(ocrError);
            toast.error(ocrError.response?.data?.message || t("Failed to upload OCR report"));
            return false;
        }
    };

    const runGenerateReport = async (targetTests) => {
        const isHospital = apiData?.data?.isHospitalPatient === true || data?.isHospitalPatient === true;
        const reportDoctor = await resolveClinicDoctor(apiData?.data?.doctor);
        const testIds = targetTests.map(test => test.testId);
        setData({ _id: id, testId: testIds });

        if (isHospital) {
            const missingType = targetTests.find(test => !getTestReportType(test._id));
            if (missingType) {
                toast.warning(t("Please select a report type (Provisional or Urgent)!"));
                return false;
            }

            for (const test of targetTests) {
                const selectedType = getTestReportType(test._id);
                const payload = {
                    testOrderId: id,
                    testId: [test.testId],
                    ...clinicDoctorPayload(reportDoctor),
                };
                const res = await generateTestReport(payload);
                if (!res.data.success) {
                    toast.warning(res.data.message || `${t("Failed to generate report")} (${test.name || t("Test")})`);
                    return false;
                }

                const uploaded = await uploadHospitalOcr(res, selectedType);
                if (!uploaded) return false;
            }

            toast.success(t("Report generated successfully!"));
            setShowResult(true);
            return true;
        }

        const payload = {
            testOrderId: id,
            testId: testIds,
            ...clinicDoctorPayload(reportDoctor),
        };
        const res = await generateTestReport(payload);
        if (!res.data.success) {
            toast.warning(res.data.message || t("Failed to generate report"));
            return false;
        }

        toast.success(t("Report generated successfully!"));
        setShowResult(true);
        return true;
    };

    const onGlobalSubmit = async () => {
        if (isSubmitting) return;

        const isValid = await trigger();

        if (!isValid) {
            toast.warning(t("Please fill all required fields before submitting!"));
            return;
        }

        const values = getValues();
        setLoading(true);
        setIsSubmitting(true);
        try {
            const reportDoctor = await resolveClinicDoctor(apiData?.data?.doctor);
            const payload = {
                ...clinicDoctorPayload(reportDoctor),
                allTests: allTests.map(test => ({
                    _id: test._id,
                    parameterGroups: test.parameterGroups.map(group => ({
                        _id: group._id,
                        parameters: group.parameters.map(param => {
                            const fieldName = test.isPackage
                                ? `pkg_${test.packageId._id}_test_${test._id}_param_${param.parameterId}`
                                : `standalone_${test._id}_param_${param.parameterId}`;

                            return {
                                _id: param._id,
                                parameterId: param.parameterId,
                                name: param.name,
                                resultValue: (values[fieldName] || "").toString().trim(),
                                isHighlight: !!highlightedParams[fieldName]
                            };
                        })
                    })),
                    reportNote: testNotes[test._id] || ""
                }))
            };

            const res = await createTestOrder(id, payload);
            if (res.data.success) {
                await runGenerateReport(allTests);
            }
        } catch (error) {
            console.error(error);
            toast.warning(error.response?.data?.message || t("Failed to submit results"));
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const generateReport = async () => {
        if (isSubmitting) return;

        const isValid = await trigger();
        if (!isValid) {
            toast.warning(t("Please fill all required fields before submitting!"));
            return;
        }

        setLoading(true);
        setIsSubmitting(true);
        try {
            await runGenerateReport(allTests);
        } catch (error) {
            toast.warning(error.response?.data?.message || t("Failed to generate report"));
        } finally {
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const scrollToFirstError = (errors) => {
        if (Object.keys(errors).length > 0) {
            const firstErrorKey = Object.keys(errors)[0];
            const element = document.querySelector(`[name="${firstErrorKey}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.focus();
            }
        }
    };

    const standaloneTests = allTests.filter(t => !t.isPackage);
    const packageMap = {};
    allTests.forEach(test => {
        if (test.isPackage && test.packageId) {
            const pkgId = test.packageId._id;
            if (!packageMap[pkgId]) {
                packageMap[pkgId] = { packageName: test.packageName, tests: [] };
            }
            packageMap[pkgId].tests.push(test);
        }
    });

    const isOrderCompleted = apiData.data.status === "Completed";

    const renderReportTypeSelector = (testId) => {
        const selected = getTestReportType(testId);
        const disabled = isOrderCompleted || isSubmitting;

        return (
            <div
                className="d-flex align-items-center gap-2 flex-shrink-0 rounded-3 px-2 py-1"
                // style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB' }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setTestReportType(testId, 'provisional')}
                    className="btn btn-sm d-flex align-items-center gap-1 rounded-pill px-2 py-1"
                    style={{
                        border: selected === 'provisional' ? '1px solid #2563EB' : '1px solid #E5E7EB',
                        backgroundColor: selected === 'provisional' ? '#EFF6FF' : '#FFFFFF',
                        color: selected === 'provisional' ? '#1E40AF' : '#1F2937',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: selected === 'provisional' ? '0 1px 3px rgba(37, 99, 235, 0.12)' : 'none'
                    }}
                >
                    {selected === 'provisional' ? (
                        <CheckCircle2 size={13} color="#2563EB" />
                    ) : (
                        <Circle size={13} color="#9CA3AF" />
                    )}
                    {t("Provisional")}
                </button>
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => setTestReportType(testId, 'urgent')}
                    className="btn btn-sm d-flex align-items-center gap-1 rounded-pill px-2 py-1"
                    style={{
                        border: selected === 'urgent' ? '1px solid #DC2626' : '1px solid #FECACA',
                        backgroundColor: selected === 'urgent' ? '#FEE2E2' : '#FEF2F2',
                        color: '#991B1B',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        transition: 'all 0.2s ease-in-out',
                        boxShadow: selected === 'urgent' ? '0 1px 3px rgba(220, 38, 38, 0.12)' : 'none'
                    }}
                >
                    {selected === 'urgent' ? (
                        <CheckCircle2 size={13} color="#DC2626" />
                    ) : (
                        <Circle size={13} color="#DC2626" />
                    )}
                    {t("Urgent")}
                </button>
            </div>
        );
    };

    const renderTestActions = (testId) => (
        <div className="py-2 px-3 text-end border-top">
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); saveSingleTest(testId); }}
                className="btn btn-primary btn-sm px-3"
                disabled={isOrderCompleted || isSubmitting}
            >
                {t("Save")}
            </button>
        </div>
    );

    return (
        <>
            <Breadcrumbs mainTitle={t("Add Test Result")} parent={t("Home")} subParents={t("Test List")} title={t("Add Test Result")} action={importFileButton} />
            <Container fluid={true}>
                <Row>
                    <Col>
                        <div className="py-2 py-md-3">
                            <Card className="border-success border-2 mb-3 shadow-sm">
                                <div className="p-2 p-sm-3">
                                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                        <div className="d-flex align-items-center gap-3 min-w-0">
                                            <S3ImageLoader fileName={patient?.profile_picture} fallbackSrc={errorProfile} className="img-50 rounded-circle flex-shrink-0" alt={t("Patient")} />
                                            <div className="min-w-0">
                                                <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: '1.05rem' }}>{patient.name}</h6>
                                                <small className="text-muted text-capitalize d-block">{patient.gender}, {t("Age")} {patient.age}</small>
                                            </div>
                                        </div>
                                        <div className="d-flex flex-wrap align-items-center gap-3 gap-sm-4 ms-auto ms-sm-0">
                                            {patient.contact !== "" && (
                                                <div className="d-flex align-items-center gap-2 tw-text-sm"><FiPhoneCall className="text-primary flex-shrink-0" /> <span className="text-dark fw-medium">{patient.contact}</span></div>
                                            )}
                                            {patient.email !== "" && (
                                                <div className="d-flex align-items-center gap-2 tw-text-sm text-break"><TfiEmail className="text-primary flex-shrink-0" /> <span className="text-dark fw-medium">{patient.email}</span></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            <form onSubmit={(e) => { e.preventDefault(); onGlobalSubmit(); }}>
                                <div className="d-flex flex-column gap-3">

                                    {/* Standalone Tests */}
                                    {standaloneTests.map(test => (
                                        <div key={test._id} className="card shadow-sm rounded-3 overflow-hidden border border-success mb-0">
                                            <div className="card-header px-3 py-2.5 d-flex flex-wrap justify-content-between align-items-center cursor-pointer bg-light-subtle gap-2" onClick={() => toggleTest(test._id)}>
                                                <div className="d-flex align-items-center gap-3 min-w-0">
                                                    <span className="badge bg-primary px-3 py-1.5 fw-bold flex-shrink-0">{test.code}</span>
                                                    <h6 className="mb-0 fw-bold text-dark text-truncate tw-text-sm">{test.name}</h6>
                                                </div>
                                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                    {isHospitalPatient && renderReportTypeSelector(test._id)}
                                                    {expandedTests[test._id] ? <ChevronUp size={20} className="text-secondary flex-shrink-0" /> : <ChevronDown size={20} className="text-secondary flex-shrink-0" />}
                                                </div>
                                            </div>

                                            {expandedTests[test._id] && (
                                                <>
                                                    <div onClick={e => e.stopPropagation()} className="card-body p-3">
                                                        <div className="table-responsive">
                                                            <table className="table table-sm align-middle mb-0">
                                                                <thead>
                                                                    <tr className="text-muted border-bottom">
                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '150px' }}>{t("Parameter")}</th>
                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '180px' }}>{t("Result")} *</th>
                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '150px' }}>{t("Reference Range")}</th>
                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '80px' }}>{t("Unit")}</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {test.parameterGroups?.map(group => group.parameters?.map(param => {
                                                                        const originalParam = test.testId?.parameterGroups
                                                                            ?.flatMap(g => g.parameters || [])
                                                                            .find(p => p.parameterId === param.parameterId);
                                                                        const range = getReferenceRange(
                                                                            originalParam?.referenceRanges || param.referenceRanges,
                                                                            gender,
                                                                            age
                                                                        );
                                                                        const fieldName = `standalone_${test._id}_param_${param.parameterId}`;

                                                                        return (
                                                                            <tr key={param._id}>
                                                                                <td className="fw-medium py-2 align-middle">{param.name}</td>
                                                                                <td className="py-2 align-middle">
                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            className="form-check-input mt-0 flex-shrink-0"
                                                                                            checked={!!highlightedParams[fieldName]}
                                                                                            onChange={() => toggleHighlight(fieldName)}
                                                                                            disabled={apiData.data.status === "Completed"}
                                                                                            data-tooltip-id="name-tooltips"
                                                                                        //  id={`highlight-${fieldName}`}
                                                                                        />
                                                                                        <Tooltip id="name-tooltips" place="top" content={t("Highlight this field")} />
                                                                                        <input
                                                                                            {...register(fieldName, { required: t("Required") })}
                                                                                            type="text"
                                                                                            className={`form-control form-control-sm ${errors[fieldName] ? 'border-danger' : ''}`}
                                                                                            style={{ maxWidth: '150px', height: '32px' }}
                                                                                            disabled={apiData.data.status === "Completed"}
                                                                                            placeholder={t("Enter result")}
                                                                                            onChange={(e) => {
                                                                                                const value = e.target.value;

                                                                                                // Call RHF onChange
                                                                                                setValue(fieldName, value, { shouldValidate: true });

                                                                                                // Auto highlight logic
                                                                                                handleResultChange(fieldName, value, range);
                                                                                            }}
                                                                                        />
                                                                                    </div>
                                                                                    {errors[fieldName] && (
                                                                                        <small className="text-danger d-flex align-items-center gap-1 mt-1 tw-text-xs">
                                                                                            <AlertCircle size={13} /> {t("Required")}
                                                                                        </small>
                                                                                    )}
                                                                                </td>
                                                                                <td className="py-2 align-middle">
                                                                                    <span className="fw-bold text-success">{range}</span>
                                                                                </td>
                                                                                <td className="py-2 align-middle">
                                                                                    <span className="fw-bold">{originalParam?.unitValue || param.unitValue || '-'}</span>
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    }))}
                                                                </tbody>
                                                            </table>
                                                        </div>

                                                        <ReactQuill
                                                            theme="snow"
                                                            value={testNotes[test._id] || ''}
                                                            onChange={(content) => setTestNotes(prev => ({ ...prev, [test._id]: content }))}
                                                            placeholder={t("Add notes...")}
                                                            className="mt-3"
                                                            readOnly={apiData.data.status === "Completed"}
                                                        />
                                                    </div>

                                                    {renderTestActions(test._id)}
                                                </>
                                            )}
                                        </div>
                                    ))}

                                    {/* Packages */}
                                    {Object.entries(packageMap).map(([packageId, pkg]) => (
                                        <div key={packageId} className="card shadow-sm rounded-3 overflow-hidden border border-success mb-0">
                                            <div
                                                className="card-header px-3 py-2.5 d-flex justify-content-between align-items-center cursor-pointer bg-light-subtle"
                                                onClick={() => togglePackage(packageId)}
                                            >
                                                <div className="d-flex align-items-center gap-2.5 min-w-0">
                                                    <Package size={22} className="text-primary flex-shrink-0" />
                                                    <h5 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: '1.05rem' }}>{pkg.packageName}</h5>
                                                    <span className="badge bg-primary px-2.5 py-1 ms-1 flex-shrink-0">{pkg.tests.length} {t("Tests")}</span>
                                                </div>
                                                {expandedPackages[packageId] ? <ChevronUp size={20} className="text-secondary flex-shrink-0" /> : <ChevronDown size={20} className="text-secondary flex-shrink-0" />}
                                            </div>

                                            {expandedPackages[packageId] && (
                                                <div className="p-3">
                                                    {pkg.tests.map(test => (
                                                        <div key={test._id} className="bg-white rounded-3 shadow-sm mb-3 border overflow-hidden">
                                                            <div
                                                                className="px-3 py-2 border-bottom d-flex flex-wrap justify-content-between align-items-center cursor-pointer bg-light-subtle gap-2"
                                                                onClick={() => toggleTest(test._id)}
                                                            >
                                                                <div className="d-flex align-items-center gap-3 min-w-0">
                                                                    <span className="badge bg-primary px-2.5 py-1 me-1 flex-shrink-0">{test.code}</span>
                                                                    <h6 className="mb-0 fw-semibold text-dark text-truncate">{test.name}</h6>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                                                    {isHospitalPatient && renderReportTypeSelector(test._id)}
                                                                    {expandedTests[test._id] ? <ChevronUp size={18} className="text-secondary flex-shrink-0" /> : <ChevronDown size={18} className="text-secondary flex-shrink-0" />}
                                                                </div>
                                                            </div>

                                                            {expandedTests[test._id] && (
                                                                <>
                                                                    <div onClick={e => e.stopPropagation()} className="p-3">
                                                                        <div className="table-responsive">
                                                                            <table className="table table-sm align-middle mb-0">
                                                                                <thead>
                                                                                    <tr className="text-muted border-bottom">
                                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '150px' }}>{t("Parameter")}</th>
                                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '180px' }}>{t("Result")} *</th>
                                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '150px' }}>{t("Reference Range")}</th>
                                                                                        <th className="py-2 text-secondary fw-semibold" style={{ minWidth: '80px' }}>{t("Unit")}</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {test.parameterGroups?.map(group => group.parameters?.map(param => {
                                                                                        const originalParam = test.testId?.parameterGroups
                                                                                            ?.flatMap(g => g.parameters || [])
                                                                                            .find(p => p.parameterId === param.parameterId);
                                                                                        const range = getReferenceRange(
                                                                                            originalParam?.referenceRanges || param.referenceRanges,
                                                                                            gender,
                                                                                            age
                                                                                        );
                                                                                        const fieldName = `pkg_${packageId}_test_${test._id}_param_${param.parameterId}`;

                                                                                        return (
                                                                                            <tr key={param._id}>
                                                                                                <td className="fw-medium py-2 align-middle">{param.name}</td>
                                                                                                <td className="py-2 align-middle">
                                                                                                    <div className="d-flex align-items-center gap-2">
                                                                                                        <input
                                                                                                            type="checkbox"
                                                                                                            className="form-check-input mt-0 flex-shrink-0"
                                                                                                            checked={!!highlightedParams[fieldName]}
                                                                                                            onChange={() => toggleHighlight(fieldName)}
                                                                                                            disabled={apiData.data.status === "Completed"}
                                                                                                            data-tooltip-id="name-tooltipPackage"
                                                                                                        />
                                                                                                        <Tooltip id="name-tooltipPackage" place="top" content={t("Highlight this field")} />
                                                                                                        <input
                                                                                                            {...register(fieldName, { required: t("Required") })}
                                                                                                            type="text"
                                                                                                            className={`form-control form-control-sm ${errors[fieldName] ? 'border-danger' : ''}`}
                                                                                                            style={{ maxWidth: '150px', height: '32px' }}
                                                                                                            disabled={apiData.data.status === "Completed"}
                                                                                                            placeholder={t("Enter result")}
                                                                                                            onChange={(e) => {
                                                                                                                const value = e.target.value;

                                                                                                                // Call RHF onChange
                                                                                                                setValue(fieldName, value, { shouldValidate: true });

                                                                                                                // Auto highlight logic
                                                                                                                handleResultChange(fieldName, value, range);
                                                                                                            }}
                                                                                                        />
                                                                                                    </div>
                                                                                                    {errors[fieldName] && (
                                                                                                        <small className="text-danger d-flex align-items-center gap-1 mt-1 tw-text-xs">
                                                                                                            <AlertCircle size={13} /> {t("Required")}
                                                                                                        </small>
                                                                                                    )}
                                                                                                </td>
                                                                                                <td className="py-2 align-middle">
                                                                                                    <span className="fw-bold text-success">{range}</span>
                                                                                                </td>
                                                                                                <td className="py-2 align-middle">
                                                                                                    <span className="fw-bold">{originalParam?.unitValue || param.unitValue || '-'}</span>
                                                                                                </td>
                                                                                            </tr>
                                                                                        );
                                                                                    }))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>

                                                                        <ReactQuill
                                                                            theme="snow"
                                                                            value={testNotes[test._id] || ''}
                                                                            onChange={(content) => setTestNotes(prev => ({ ...prev, [test._id]: content }))}
                                                                            placeholder={t("Add notes...")}
                                                                            className="mt-3"
                                                                            readOnly={apiData.data.status === "Completed"}
                                                                        />
                                                                    </div>

                                                                    {renderTestActions(test._id)}
                                                                </>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="d-flex justify-content-end mt-4 mb-2">
                                    <div className="d-flex align-items-center gap-2">
                                        {apiData.data.status === "Submitted" ? (
                                            <button
                                                type="button"
                                                className="btn btn-primary px-4 py-2"
                                                onClick={generateReport}
                                                disabled={isSubmitting}
                                            >
                                                {t("Generate Report")}
                                            </button>
                                        ) : (
                                            <button
                                                type="submit"
                                                className="btn btn-primary px-4 py-2"
                                                disabled={isOrderCompleted || isSubmitting}
                                            >
                                                {t("Submit All Results")}
                                            </button>
                                        )}
                                        <Link to={`${process.env.PUBLIC_URL}/test-list`}>
                                            <button type="button" className="btn btn-secondary px-3 py-2">{t("Cancel")}</button>
                                        </Link>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </Col>
                </Row>
            </Container>

            <Modal show={showResult} onHide={handleCloseResult} centered scrollable>
                <ReportList setUploadModelOpen={handleCloseResult} toggleUploadModel={handleCloseResult} data={data} />
            </Modal>
        </>
    );
}